import "@/App.css";
import { useEffect } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { GuildProvider, useGuild } from "@/contexts/GuildContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import MusicStats from "@/pages/MusicStats";
import ServerStats from "@/pages/ServerStats";
import BotUsage from "@/pages/BotUsage";
import Moderation from "@/pages/Moderation";
import Settings from "@/pages/Settings";
import GameAlerts from "@/pages/GameAlerts";
import BotLog from "@/pages/BotLog";
import BanAppeal from "@/pages/BanAppeal";
import NowPlayingWidget from "@/pages/NowPlayingWidget";
import Soundboard from "@/pages/Soundboard";
import Giveaways from "@/pages/Giveaways";
import Tickets from "@/pages/Tickets";
import ServerLogs from "@/pages/ServerLogs";
import Economy from "@/pages/Economy";
import AutoMod from "@/pages/AutoMod";
import Birthdays from "@/pages/Birthdays";
import Invites from "@/pages/Invites";
import CommandPermissions from "@/pages/CommandPermissions";
import SongRequests from "@/pages/SongRequests";
import MemberHub from "@/pages/MemberHub";
import Support from "@/pages/Support";
import { Toaster } from "@/components/ui/sonner";

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        <p className="text-zinc-400 font-[Manrope]">Loading dashboard...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function StaffRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { selectedGuild, loading: guildLoading } = useGuild();
  if (authLoading || guildLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!selectedGuild?.is_staff) return <Navigate to="/" replace />;
  return children;
}

function FullRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { selectedGuild, loading: guildLoading } = useGuild();
  if (authLoading || guildLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (selectedGuild?.limited) return <Navigate to="/" replace />;
  return children;
}

function HomeRoute() {
  const { user, loading: authLoading } = useAuth();
  const { selectedGuild, loading: guildLoading } = useGuild();
  if (authLoading || guildLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return selectedGuild?.limited ? <MemberHub /> : <Dashboard />;
}

function ModRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { selectedGuild, loading: guildLoading } = useGuild();
  if (authLoading || guildLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!selectedGuild?.is_mod && !selectedGuild?.is_staff) return <Navigate to="/" replace />;
  return children;
}

function App() {
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API}/api/bot/status`)
      .then(r => {
        const avatar = r.data?.bot_avatar;
        if (avatar) {
          const link = document.getElementById('favicon');
          if (link) link.href = avatar;
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <ThemeProvider>
      <AuthProvider>
        <GuildProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/" element={<HomeRoute />} />
              <Route path="/music" element={<FullRoute><MusicStats /></FullRoute>} />
              <Route path="/server" element={<FullRoute><ServerStats /></FullRoute>} />
              <Route path="/usage" element={<FullRoute><BotUsage /></FullRoute>} />
              <Route path="/game-alerts" element={<StaffRoute><GameAlerts /></StaffRoute>} />
              <Route path="/moderation" element={<ModRoute><Moderation /></ModRoute>} />
              <Route path="/settings" element={<StaffRoute><Settings /></StaffRoute>} />
              <Route path="/soundboard" element={<FullRoute><Soundboard /></FullRoute>} />
              <Route path="/giveaways" element={<FullRoute><Giveaways /></FullRoute>} />
              <Route path="/tickets" element={<ModRoute><Tickets /></ModRoute>} />
              <Route path="/server-logs" element={<ModRoute><ServerLogs /></ModRoute>} />
              <Route path="/economy" element={<FullRoute><Economy /></FullRoute>} />
              <Route path="/bot-log" element={<FullRoute><BotLog /></FullRoute>} />
              <Route path="/automod" element={<StaffRoute><AutoMod /></StaffRoute>} />
              <Route path="/birthdays" element={<StaffRoute><Birthdays /></StaffRoute>} />
              <Route path="/invites" element={<FullRoute><Invites /></FullRoute>} />
              <Route path="/commands" element={<StaffRoute><CommandPermissions /></StaffRoute>} />
              <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
              <Route path="/song-requests" element={<ProtectedRoute><SongRequests /></ProtectedRoute>} />
              <Route path="/appeal/:guildId" element={<ProtectedRoute><BanAppeal /></ProtectedRoute>} />
              <Route path="/widget/:guildId" element={<NowPlayingWidget />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </GuildProvider>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
